import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { useMerchantOps } from '@/api/hooks/useMerchants';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';

const ZAR = new Intl.NumberFormat('en-ZA', {
  style: 'currency',
  currency: 'ZAR',
  maximumFractionDigits: 0,
});
const NUM = new Intl.NumberFormat('en-ZA', { maximumFractionDigits: 0 });
const RPL = new Intl.NumberFormat('en-ZA', { maximumFractionDigits: 2 });

export default function MerchantOps() {
  const { data, isLoading, error } = useMerchantOps();

  return (
    <div className="flex h-full flex-col gap-4">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">Merchant Ops</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Top spenders and fleet-wide pricing trend (last 12 months).
        </p>
      </div>

      {error && (
        <Card className="border-red-200 bg-red-50">
          <CardContent className="py-3 text-sm text-red-800">
            Could not load merchant ops: {(error as Error).message}
          </CardContent>
        </Card>
      )}

      <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm">Top 20 merchants by 90-day spend</CardTitle>
          </CardHeader>
          <CardContent className="h-72 px-2 py-2">
            {isLoading ? (
              <Skeleton className="h-full w-full" />
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  data={data?.top_by_spend ?? []}
                  layout="vertical"
                  margin={{ top: 4, right: 16, bottom: 4, left: 70 }}
                >
                  <CartesianGrid stroke="hsl(var(--border))" strokeDasharray="3 3" />
                  <XAxis
                    type="number"
                    tick={{ fontSize: 10 }}
                    tickFormatter={(v: number) =>
                      v >= 1_000_000
                        ? `${(v / 1_000_000).toFixed(1)}M`
                        : v >= 1_000
                          ? `${(v / 1_000).toFixed(0)}k`
                          : `${v}`
                    }
                  />
                  <YAxis
                    type="category"
                    dataKey={(d) => d.trade_name ?? d.merchant_code}
                    tick={{ fontSize: 10 }}
                    width={70}
                  />
                  <Tooltip formatter={(v: number) => ZAR.format(v)} />
                  <Bar dataKey="spend_rands" fill="hsl(var(--primary))" />
                </BarChart>
              </ResponsiveContainer>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm">Fleet R/L price trend (12 months)</CardTitle>
          </CardHeader>
          <CardContent className="h-72 px-2 py-2">
            {isLoading ? (
              <Skeleton className="h-full w-full" />
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <LineChart
                  data={data?.price_trend ?? []}
                  margin={{ top: 4, right: 8, bottom: 4, left: 0 }}
                >
                  <CartesianGrid stroke="hsl(var(--border))" strokeDasharray="3 3" />
                  <XAxis
                    dataKey="month"
                    tick={{ fontSize: 10 }}
                    tickFormatter={(v: string) => v.slice(0, 7)}
                  />
                  <YAxis
                    tick={{ fontSize: 10 }}
                    width={50}
                    tickFormatter={(v: number) => `R${v.toFixed(1)}`}
                  />
                  <Tooltip
                    formatter={(v: number) => `R ${RPL.format(v)}`}
                    labelFormatter={(v: string) => `Month: ${v.slice(0, 7)}`}
                  />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                  <Line
                    type="monotone"
                    dataKey="fleet_avg_rand_per_litre"
                    name="Fleet avg R/L"
                    stroke="hsl(var(--primary))"
                    strokeWidth={2}
                    dot={false}
                  />
                  <Line
                    type="monotone"
                    dataKey="p90_rand_per_litre"
                    name="P90 R/L"
                    stroke="hsl(var(--destructive))"
                    strokeWidth={1.5}
                    strokeDasharray="4 4"
                    dot={false}
                  />
                </LineChart>
              </ResponsiveContainer>
            )}
          </CardContent>
        </Card>
      </div>

      <Card className="flex-1">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm">Top 20 — detail</CardTitle>
        </CardHeader>
        <CardContent className="overflow-x-auto px-0">
          {isLoading ? (
            <div className="space-y-2 px-6 pb-4">
              {Array.from({ length: 8 }).map((_, i) => (
                <Skeleton key={i} className="h-7 w-full" />
              ))}
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Merchant</TableHead>
                  <TableHead>Brand</TableHead>
                  <TableHead className="text-right">Txns</TableHead>
                  <TableHead className="text-right">Spend (90d)</TableHead>
                  <TableHead className="text-right">Avg R/L</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data?.top_by_spend.map((m) => (
                  <TableRow key={m.merchant_id}>
                    <TableCell>
                      <div className="font-medium">{m.trade_name ?? m.merchant_code}</div>
                      <div className="text-xs text-muted-foreground">{m.merchant_code}</div>
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {m.brand ?? '—'}
                    </TableCell>
                    <TableCell className="text-right tabular-nums">
                      {NUM.format(m.txn_count)}
                    </TableCell>
                    <TableCell className="text-right tabular-nums">
                      {ZAR.format(m.spend_rands)}
                    </TableCell>
                    <TableCell className="text-right tabular-nums">
                      {m.avg_rand_per_litre != null
                        ? `R ${RPL.format(m.avg_rand_per_litre)}`
                        : '—'}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
